/**
 * AutoSellThresholdControl
 * Dropdown control for setting the auto-sell quality threshold.
 * Fish below the selected quality are auto-sold by "Sell All" and Haulers.
 * Fish at or above the threshold are kept (along with per-fish locked stacks).
 */

import { useGameStore } from '@/state';
import { Quality, QualityColors } from '@/types';
import { saveManager } from '@/systems/persistence/SaveManager';

/** Quality options for the threshold dropdown (excludes QUEST — not player-facing) */
const THRESHOLD_OPTIONS: { value: Quality | ''; label: string; color?: string }[] = [
  { value: '', label: 'Disabled (keep everything)' },
  { value: Quality.UNCOMMON, label: `Uncommon+ kept (auto-sell Common)`, color: QualityColors[Quality.UNCOMMON] },
  { value: Quality.RARE, label: `Rare+ kept (auto-sell Common, Uncommon)`, color: QualityColors[Quality.RARE] },
  { value: Quality.EPIC, label: `Epic+ kept (auto-sell up to Rare)`, color: QualityColors[Quality.EPIC] },
  { value: Quality.LEGENDARY, label: `Legendary+ kept (auto-sell up to Epic)`, color: QualityColors[Quality.LEGENDARY] },
];

export function AutoSellThresholdControl() {
  const threshold = useGameStore((s) => s.playerState.autoSellSettings.qualityThreshold);
  const setThreshold = useGameStore((s) => s.setAutoSellThreshold);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setThreshold(value ? (value as Quality) : null);
    saveManager.save();
  };

  return (
    <div className="bg-gray-800/60 rounded-lg p-4 border border-gray-700">
      <div className="flex items-center justify-between gap-3 mb-2">
        <label htmlFor="auto-sell-threshold" className="text-sm font-medium text-gray-300">
          Auto-Sell Below Quality
        </label>
      </div>

      <select
        id="auto-sell-threshold"
        value={threshold ?? ''}
        onChange={handleChange}
        className="w-full bg-gray-900 text-white border border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        style={threshold ? { borderColor: QualityColors[threshold] + '80' } : undefined}
      >
        {THRESHOLD_OPTIONS.map((opt) => (
          <option
            key={opt.value}
            value={opt.value}
            style={opt.color ? { color: opt.color } : undefined}
          >
            {opt.label}
          </option>
        ))}
      </select>

      <p className="text-xs text-gray-500 mt-2">
        Haulers and "Sell All" will skip fish at or above this quality. Per-fish locks override this setting.
      </p>
    </div>
  );
}
